import React, { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

// UI

import {
  Box,
  Button,
  Flex,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  Checkbox,
} from '@chakra-ui/react'

// Custom
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

import useFile from '@/features/files/useFile'
import { selectApprovalRemarkModal } from '@/stores/offers/approvalRemarkModalSlice'
import { hideApprovalRemarkModal } from '@/stores/offers/offersSlice'
import { allFileTypes } from '@/utils/constants'
import { isNullish } from '@/utils/helper'
import { camelToWords } from '@/utils/stringHelper'
import { OfferDocumentTypes } from '../../constants'
import useOffer from '../../hooks/useOffer'
import useOfferApproval from '../../hooks/useOfferApproval'
import useOfferPR from '@/features/offers/hooks/useOfferPR'
import useOfferGCPR from '@/features/offers/hooks/useOfferGCPR'
import { OfferApprovalType, PRApprovalType } from '@eapproval/offer-shared'

const ApprovalRemarkModal = () => {
  const { isOpened, approvalType, offerId, approverType, approverLevel, updatePath, remark, meta } =
    useSelector(selectApprovalRemarkModal)

  const [files, setFiles] = useState([])
  const [noticePreviousUser, setNoticePreviousUser] = useState(false)
  const [rollbackToHSP2, setRollbackToHSP2] = useState(false)

  const fileInput = useRef()
  const remarkRef = useRef()
  const navigate = useNavigate()

  const {
    useContributorReview,
    useContributorComment,
    useApproveOfferQuotation,
    useRejectOfferQuotation,
    useApproveOfferBooking,
    useRejectOfferBooking,
  } = useOfferApproval()
  const { useApprovePR, useOnHoldPR, useRejectPR, useUpdatePRSubmission } = useOfferPR()
  const { useApproveGCMaterialPR, useRejectGCMaterialPR, useUpdateGCMaterialPR } = useOfferGCPR()

  const { usePatchOffer } = useOffer()
  const { useUploadFile } = useFile()
  const dispatch = useDispatch()

  const contributorReview = useContributorReview()
  const contributorComment = useContributorComment()
  const approveOfferQuotation = useApproveOfferQuotation()
  const rejectOfferQuotation = useRejectOfferQuotation()
  const approveBooking = useApproveOfferBooking()
  const rejectBooking = useRejectOfferBooking()
  const approvePR = useApprovePR()
  const rejectPR = useRejectPR()
  const onHoldPR = useOnHoldPR()
  const updatePRSubmission = useUpdatePRSubmission()
  const approveGCMaterialPR = useApproveGCMaterialPR()
  const rejectGCMaterialPR = useRejectGCMaterialPR()
  const updateGCMaterialPR = useUpdateGCMaterialPR()

  const patchOffer = usePatchOffer()

  const uploadFile = useUploadFile()

  const closeModal = () => {
    setFiles([])
    dispatch(hideApprovalRemarkModal())
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    const remarkValue = remarkRef.current.value
    let uploadedAttachmentIds = []

    if (
      [
        OfferApprovalType.CONTRIBUTION_COMMENT,
        OfferApprovalType.QUOTATION_REJECT,
        OfferApprovalType.BOOKING_REJECT,
      ].includes(approvalType) &&
      remarkValue === ''
    ) {
      toast.error(`You need to enter insert comment!`)
      return
    }

    const payload = {
      offerId: offerId,
      remark: remarkValue,
      files: files,

      //For Approval
      approverType: approverType,
      approverLevel: approverLevel,
    }

    try {
      dispatch(showLoadingOverlay())

      if (
        [
          OfferApprovalType.GCPR_APPROVE,
          OfferApprovalType.GCPR_REJECT,
          OfferApprovalType.PR_APPROVE,
          OfferApprovalType.PR_REJECT,
        ].includes(approvalType) &&
        files.length > 0
      ) {
        const uploadResult = await uploadFile.mutateAsync({
          version: 0,
          type: OfferDocumentTypes.REPLY_SUPPORT_DOCUMENT,
          files: files,
          isGuest: false,
        })

        uploadedAttachmentIds = Array.isArray(uploadResult?.data)
          ? uploadResult.data.map((fileDetail) => fileDetail?._id).filter((id) => !!id)
          : []
      }

      switch (approvalType) {
        case OfferApprovalType.CONTRIBUTION_REVIEW:
        case OfferApprovalType.OPTIONAL_CONTRIBUTION_REVIEW:
          await contributorReview.mutateAsync(payload)
          break
        case OfferApprovalType.CONTRIBUTION_COMMENT:
          await contributorComment.mutateAsync(payload)

          break
        case OfferApprovalType.QUOTATION_APPROVE:
          await approveOfferQuotation.mutateAsync(payload)
          break
        case OfferApprovalType.QUOTATION_REJECT:
          await rejectOfferQuotation.mutateAsync(payload)
          break

        case OfferApprovalType.BOOKING_APPROVE:
          await approveBooking.mutateAsync(payload)
          break
        case OfferApprovalType.BOOKING_REJECT:
          await rejectBooking.mutateAsync(payload)
          break

        case OfferApprovalType.PR_APPROVE:
          if (
            meta?.costBudgetIndex === undefined ||
            meta?.submissionIndex === undefined ||
            meta?.vendorSelectionIndex === undefined
          ) {
            throw new Error('Missing Cost Budget Index or Submission Index')
          }

          if (meta?.submissionDetail) {
            await updatePRSubmission.mutateAsync({
              offerId,
              costBudgetIndex: meta.costBudgetIndex,
              vendorSelectionIndex: meta.vendorSelectionIndex,
              submissionIndex: meta.submissionIndex,
              submissionDetail: meta.submissionDetail,
            })
          }

          await approvePR.mutateAsync({
            offerId: offerId,
            costBudgetIndex: meta?.costBudgetIndex,
            vendorSelectionIndex: meta?.vendorSelectionIndex,
            submissionIndex: meta?.submissionIndex,
            remark: remarkValue,
            attachments: uploadedAttachmentIds,
          })
          break
        case OfferApprovalType.PR_REJECT:
          if (
            meta?.costBudgetIndex === undefined ||
            meta?.submissionIndex === undefined ||
            meta?.vendorSelectionIndex === undefined
          ) {
            throw new Error('Missing Cost Budget Index or Submission Index')
          }

          if (meta?.submissionDetail) {
            await updatePRSubmission.mutateAsync({
              offerId,
              costBudgetIndex: meta.costBudgetIndex,
              vendorSelectionIndex: meta.vendorSelectionIndex,
              submissionIndex: meta.submissionIndex,
              submissionDetail: meta.submissionDetail,
            })
          }

          await rejectPR.mutateAsync({
            offerId: offerId,
            costBudgetIndex: meta?.costBudgetIndex,
            vendorSelectionIndex: meta?.vendorSelectionIndex,

            submissionIndex: meta?.submissionIndex,
            remark: remarkValue,
            noticePreviousUser: noticePreviousUser,
            rollbackToHSP2: rollbackToHSP2,
            attachments: uploadedAttachmentIds,
          })
          break
        case OfferApprovalType.PR_ON_HOLD:
          if (
            meta?.costBudgetIndex === undefined ||
            meta?.submissionIndex === undefined ||
            meta?.vendorSelectionIndex === undefined
          ) {
            throw new Error('Missing Cost Budget Index or Submission Index')
          }
          if (meta?.submissionDetail) {
            await updatePRSubmission.mutateAsync({
              offerId,
              costBudgetIndex: meta.costBudgetIndex,
              vendorSelectionIndex: meta.vendorSelectionIndex,
              submissionIndex: meta.submissionIndex,
              submissionDetail: meta.submissionDetail,
            })
          }

          await onHoldPR.mutateAsync({
            offerId: offerId,
            costBudgetIndex: meta.costBudgetIndex,
            submissionIndex: meta.submissionIndex,
            vendorSelectionIndex: meta.vendorSelectionIndex,
            remark: remarkValue,
          })

          break
        case OfferApprovalType.GCPR_APPROVE:
          if (meta?.submissionIndex === undefined) {
            throw new Error('Missing Cost Budget Index or Submission Index')
          }

          if (meta?.submissionDetail) {
            await updateGCMaterialPR.mutateAsync({
              offerId,
              submissionIndex: meta.submissionIndex,
              submissionDetail: meta.submissionDetail,
            })
          }

          await approveGCMaterialPR.mutateAsync({
            offerId: offerId,
            submissionIndex: meta?.submissionIndex,
            remark: remarkValue,
            attachments: uploadedAttachmentIds,
          })
          break
        case OfferApprovalType.GCPR_REJECT:
          if (meta?.submissionIndex === undefined) {
            throw new Error('Missing Cost Budget Index or Submission Index')
          }
          if (meta?.submissionDetail) {
            await updateGCMaterialPR.mutateAsync({
              offerId,
              submissionIndex: meta.submissionIndex,
              submissionDetail: meta.submissionDetail,
            })
          }

          await rejectGCMaterialPR.mutateAsync({
            offerId: offerId,
            submissionIndex: meta?.submissionIndex,
            remark: remarkValue,
            attachments: uploadedAttachmentIds,
          })
          break
        default:
          throw new Error('Unknown Modal Type ! ')
      }
      closeModal()
      navigate(-1)
    } catch (error) {
      closeModal()

      console.log(error)
      const errorResponseObj = error?.response?.data

      if (errorResponseObj?.type === 'MISSING_SIGNATURE') {
        toast.error(
          <Box>
            <Text>{errorResponseObj.message}</Text>
          </Box>
        )
      } else if (errorResponseObj?.message) {
        toast.error(errorResponseObj.message)
      }
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleSaveDraft = async (e) => {
    try {
      e.preventDefault()
      dispatch(showLoadingOverlay)

      const remarkValue = remarkRef.current.value

      const patchData = []

      patchData.push({
        op: 'replace',
        path: `${updatePath}.remark`,
        value: remarkValue,
      })

      //Upload the file first to get the file Id

      if (files.length > 0) {
        const uploadResult = await uploadFile.mutateAsync({
          version: 0,
          type: OfferDocumentTypes.REPLY_SUPPORT_DOCUMENT,
          files: files,
          isGuest: false,
        })

        if (Array.isArray(uploadResult?.data)) {
          const uploadFileIds = uploadResult?.data.map((fileDetail) => fileDetail?._id)
          patchData.push({
            op: 'add',
            path: `${updatePath}.attachments`,
            value: uploadFileIds,
          })
        }
      }

      if (isNullish(offerId) || offerId === '') {
        throw new Error('Missing Offer Id')
      }

      await patchOffer.mutateAsync({ offerId, payload: patchData, files })
      toast.info(`Successfully save comments`)

      closeModal()
    } catch (err) {
      console.error(err)
      toast.error(err?.message || 'Error in saving draft')
    } finally {
      dispatch(hideLoadingOverlay())
    }
  }

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setFiles(e.target.files)
    }
  }

  return (
    <Modal isOpen={isOpened} onClose={closeModal} size="xl">
      <form onSubmit={onSubmit}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{camelToWords(approvalType)} Offer</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap="2">
              <p>Comment: </p>
              <Textarea name="remark" rows="5" ref={remarkRef} defaultValue={remark} />

              <Flex gap="3">
                <Text size="sm">Documents:</Text>
                <input
                  type="file"
                  accept={allFileTypes}
                  name="file"
                  ref={fileInput}
                  onChange={handleFileChange}
                  multiple
                />
              </Flex>

              {approvalType === OfferApprovalType.PR_REJECT && (
                <Flex gap="3" alignItems="center">
                  <Text size="sm">Notice previous approved user</Text>
                  <Checkbox onChange={(e) => setNoticePreviousUser(e.target.checked)} />
                </Flex>
              )}

              {approvalType === OfferApprovalType.PR_REJECT && approverType === PRApprovalType.HSP_1 && (
                <Flex gap="3" alignItems="center">
                  <Text size="sm">Rollback to HSP 2</Text>
                  <Checkbox onChange={(e) => setRollbackToHSP2(e.target.checked)} />
                </Flex>
              )}
            </Flex>
          </ModalBody>
          <ModalFooter>
            <HStack>
              {![OfferApprovalType.PR_APPROVE, OfferApprovalType.PR_REJECT].includes(approvalType) &&
                updatePath !== '' && (
                  <Button colorScheme={'blue'} onClick={handleSaveDraft}>
                    Save Draft
                  </Button>
                )}

              <Button
                colorScheme={
                  [
                    OfferApprovalType.CONTRIBUTION_COMMENT,
                    OfferApprovalType.BOOKING_REJECT,
                    OfferApprovalType.QUOTATION_REJECT,
                    OfferApprovalType.PR_REJECT,
                    OfferApprovalType.GCPR_REJECT,
                  ].includes(approvalType)
                    ? 'red'
                    : 'green'
                }
                type="submit"
              >
                {camelToWords(approvalType)}
              </Button>
              <Button colorScheme="gray" onClick={closeModal}>
                Close
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  )
}

export default ApprovalRemarkModal
